'use client';

import { c as _c } from "react/compiler-runtime";
import type { CustomerFunnelStage, DemandInspirationAttributes, DemandGenerationOption } from '@sizzlenz/shared';
import { DEMAND_CHANNELS, DEMAND_ROLES, DEMAND_TACTICS, getCustomerFunnelStageLabel, hasResolvedDirectoryFacets, resolveDirectoryFacetsFromDemandAttributes } from '@sizzlenz/shared';
import { ExternalLink, Heart, Lightbulb, MessageCircle, Send, Target, TrendingUp, Users } from 'lucide-react';
import Link from 'next/link';
import { LazyImage } from '@/components/LazyImage';
import { Badge } from '@/components/ui/badge';
import { PostCard } from '@/components/ui/post-card';
import { buildDirectoryHrefMulti } from '@/lib/directory-filters';
import { cn } from '@/lib/utils';
export interface DemandExample {
  id: string;
  brand: string;
  whatTheyDid: string;
  whyItWorks: string;
  source: string;
  attributes: DemandInspirationAttributes;
  /** Optional image URL. Falls back to a placeholder when empty. */
  imageUrl?: string;
}

/** Metric shown as a colored badge in the analysis panel. */
export interface PostMetric {
  value: string;
  label: string;
  /** Tailwind border/text color class, e.g. "border-green-500 text-green-600" */
  colorClass?: string;
}

/** Analysis data for the intelligence layout. */
export interface PostAnalysis {
  /** Short performance summary, e.g. "High-performing post" */
  performanceLabel: string;
  /** Key metrics (reach %, engagement %, saves, etc.) */
  metrics: PostMetric[];
  /** Bullet points explaining why the post worked */
  whyItWorked: string[];
  /** Audience signal summary */
  audienceSignal?: string;
}

/** Post metadata for the intelligence layout. */
export interface PostMeta {
  handle: string;
  timeAgo: string;
  caption?: string;
  likes?: string;
  comments?: string;
  shares?: string;
}
interface ExampleCardProps {
  example: DemandExample;
  /**
   * Layout variant:
   * - "compact" (default) — single vertical card, used in carousels
   * - "intelligence" — side-by-side post + analysis panel
   */
  variant?: 'compact' | 'intelligence';
  /** Post metadata for the intelligence layout. */
  postMeta?: PostMeta;
  /** Analysis data for the intelligence layout. */
  analysis?: PostAnalysis;
  /** Override PostCard width class for compact variant. Pass "w-full" for responsive fill. */
  widthClass?: string;
  className?: string;
}
const STAGE_BADGE_STYLES: Record<CustomerFunnelStage, string> = {
  awareness: 'border-transparent bg-[#ff8a3d] text-gray-950',
  trial: 'border-transparent bg-[#ffc857] text-gray-950',
  consideration: 'border-transparent bg-[#d9d0f4] text-gray-950',
  intent: 'border-transparent bg-[#8bd3a8] text-gray-950',
  purchase: 'border-transparent bg-[#4fc3a1] text-gray-950',
  loyalty: 'border-transparent bg-[#159a84] text-white',
  advocacy: 'border-transparent bg-[#211d4f] text-white'
};
function findOptionLabel<TId extends string>(options: readonly (DemandGenerationOption & {
  id: TId;
})[], id: TId): string {
  return options.find(option => option.id === id)?.label ?? id;
}
function JourneyTransitionBadge(t0) {
  const $ = _c(8);
  const {
    attributes
  } = t0;
  const {
    primaryTransition,
    journeyStages
  } = attributes;
  const targetStage = primaryTransition?.to ?? journeyStages[journeyStages.length - 1] ?? "awareness";
  let t1;
  if ($[0] !== journeyStages || $[1] !== primaryTransition) {
    t1 = primaryTransition ? `${getCustomerFunnelStageLabel(primaryTransition.from)} → ${getCustomerFunnelStageLabel(primaryTransition.to)}` : journeyStages.map(getCustomerFunnelStageLabel).join(" \xB7 ");
    $[0] = journeyStages;
    $[1] = primaryTransition;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const label = t1;
  const t2 = STAGE_BADGE_STYLES[targetStage];
  let t3;
  if ($[3] !== t2) {
    t3 = cn("font-black uppercase tracking-wider", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== label || $[6] !== t3) {
    t4 = <Badge className={t3}>{label}</Badge>;
    $[5] = label;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function DemandTaxonomyChips(t0) {
  const $ = _c(12);
  const {
    attributes
  } = t0;
  const role = attributes.demandRoles[0];
  const tactic = attributes.tactics[0];
  const channel = attributes.channels[0];
  let t1;
  if ($[0] !== role) {
    t1 = role ? findOptionLabel(DEMAND_ROLES, role) : null;
    $[0] = role;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tactic) {
    t2 = tactic ? findOptionLabel(DEMAND_TACTICS, tactic) : null;
    $[2] = tactic;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== channel) {
    t3 = channel ? findOptionLabel(DEMAND_CHANNELS, channel) : null;
    $[4] = channel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = [t1, t2, t3].filter(_temp);
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const labels = t4;
  let t5;
  if ($[10] !== labels) {
    t5 = <div className="flex flex-wrap gap-1.5" aria-label="Demand classification">{labels.map(_temp2)}</div>;
    $[10] = labels;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * "Show providers" link that resolves a record's demand attributes (tactics,
 * delivery activities, channels) to directory capability + channel-expertise
 * facets and links to a pre-filtered /directory view. Renders nothing when no
 * attribute resolves to a directory facet.
 */
function _temp2(label_0) {
  return <Badge key={label_0} variant="secondary" className="font-semibold text-gray-600">{label_0}</Badge>;
}
function _temp(label) {
  return Boolean(label);
}
function ShowProvidersLink(t0) {
  const $ = _c(6);
  const {
    attributes
  } = t0;
  let t1;
  let t2;
  if ($[0] !== attributes) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const facets = resolveDirectoryFacetsFromDemandAttributes(attributes);
      if (!hasResolvedDirectoryFacets(facets)) {
        t2 = null;
        break bb0;
      }
      t1 = buildDirectoryHrefMulti({
        capability: facets.capabilities,
        channelExpertise: facets.channelExpertise
      });
    }
    $[0] = attributes;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const href = t1;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Users className="size-3.5" aria-hidden="true" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== href) {
    t4 = <Link href={href} className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-black uppercase tracking-wider text-[#211d4f] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]">{t3}Show providers</Link>;
    $[4] = href;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function BrandPlaceholder(t0) {
  const $ = _c(3);
  const {
    brand
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-gray-200"><TrendingUp className="size-7 text-gray-400" aria-hidden="true" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== brand) {
    t2 = <div className="flex size-full items-center justify-center bg-gray-100"><div className="text-center">{t1}<p className="mt-2 text-sm font-bold text-gray-500">{brand}</p></div></div>;
    $[1] = brand;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function ExampleCard(t0) {
  const $ = _c(45);
  const {
    example,
    variant: t1,
    postMeta,
    analysis,
    widthClass,
    className
  } = t0;
  const variant = t1 === undefined ? "compact" : t1;
  if (variant === "intelligence") {
    let t2;
    if ($[0] !== analysis || $[1] !== className || $[2] !== example || $[3] !== postMeta) {
      t2 = <IntelligenceLayout example={example} postMeta={postMeta} analysis={analysis} className={className} />;
      $[0] = analysis;
      $[1] = className;
      $[2] = example;
      $[3] = postMeta;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex size-8 items-center justify-center rounded-full bg-gray-100"><TrendingUp className="size-4 text-gray-500" aria-hidden="true" /></div>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== example.brand) {
    t3 = <span className="text-sm font-bold text-gray-900">{example.brand}</span>;
    $[6] = example.brand;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ExternalLink className="size-3" aria-hidden="true" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== example.source) {
    t5 = <span className="inline-flex items-center gap-1 text-xs text-gray-500">{t4}{example.source}</span>;
    $[9] = example.source;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <>{t2}<div className="flex flex-1 items-center justify-between">{t3}{t5}</div></>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = `${example.brand} demand example`;
  let t8;
  if ($[14] !== example.brand) {
    t8 = <BrandPlaceholder brand={example.brand} />;
    $[14] = example.brand;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== example.imageUrl || $[17] !== t7 || $[18] !== t8) {
    t9 = <LazyImage src={example.imageUrl} alt={t7} className="absolute inset-0 size-full" fallback={t8} />;
    $[16] = example.imageUrl;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== example.attributes) {
    t10 = <div className="absolute left-3 top-3"><JourneyTransitionBadge attributes={example.attributes} /></div>;
    $[20] = example.attributes;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-center gap-1.5"><Target className="size-3 shrink-0 text-[#d63f20]" aria-hidden="true" /><p className="text-[10px] font-black uppercase tracking-wider text-[#d63f20]">What they did</p></div>;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== example.whatTheyDid) {
    t13 = <div>{t12}<p className="mt-1 text-sm leading-relaxed text-gray-600">{example.whatTheyDid}</p></div>;
    $[26] = example.whatTheyDid;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex items-center gap-1.5"><Lightbulb className="size-3 shrink-0 text-[#159a84]" aria-hidden="true" /><p className="text-[10px] font-black uppercase tracking-wider text-[#159a84]">Why it works</p></div>;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== example.whyItWorks) {
    t15 = <div>{t14}<p className="mt-1 text-sm leading-relaxed text-gray-600">{example.whyItWorks}</p></div>;
    $[29] = example.whyItWorks;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  let t17;
  if ($[31] !== example.attributes) {
    t16 = <DemandTaxonomyChips attributes={example.attributes} />;
    t17 = <ShowProvidersLink attributes={example.attributes} />;
    $[31] = example.attributes;
    $[32] = t16;
    $[33] = t17;
  } else {
    t16 = $[32];
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t13 || $[35] !== t15 || $[36] !== t16 || $[37] !== t17) {
    t18 = <div className="flex flex-col gap-3">{t13}{t15}{t16}{t17}</div>;
    $[34] = t13;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== className || $[40] !== t11 || $[41] !== t18 || $[42] !== t6 || $[43] !== widthClass) {
    t19 = <PostCard className={className} widthClass={widthClass} header={t6} media={t11} body={t18} />;
    $[39] = className;
    $[40] = t11;
    $[41] = t18;
    $[42] = t6;
    $[43] = widthClass;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  return t19;
}
function IntelligenceLayout(t0) {
  const $ = _c(64);
  const {
    example,
    postMeta,
    analysis,
    className
  } = t0;
  let t1;
  if ($[0] !== example.brand || $[1] !== postMeta?.handle) {
    t1 = postMeta?.handle ?? `@${example.brand.toLowerCase().replace(/[^a-z0-9]/g, "")}`;
    $[0] = example.brand;
    $[1] = postMeta?.handle;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handle = t1;
  const timeAgo = postMeta?.timeAgo ?? "2h";
  let t2;
  if ($[3] !== className) {
    t2 = cn("grid w-[320px] shrink-0 grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:w-150 sm:grid-cols-[1fr_1fr]", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex size-8 items-center justify-center rounded-full bg-gray-100"><TrendingUp className="size-4 text-gray-500" aria-hidden="true" /></div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== handle) {
    t4 = <span className="text-sm font-bold text-gray-900">{handle}</span>;
    $[6] = handle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== timeAgo) {
    t5 = <span className="text-xs text-gray-400">{timeAgo}</span>;
    $[8] = timeAgo;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="flex items-center gap-2.5 border-b border-gray-100 px-3.5 py-2.5">{t3}<div className="flex flex-1 items-center justify-between">{t4}{t5}</div></div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = example.imageUrl;
  const t8 = `${example.brand} post`;
  let t9;
  if ($[13] !== example.whatTheyDid) {
    t9 = example.whatTheyDid.split(" ").slice(0, 6).join(" ");
    $[13] = example.whatTheyDid;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <div className="flex size-full items-center justify-center p-6"><p className="text-center text-lg font-black uppercase leading-tight text-gray-800">{t9}</p></div>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== example.imageUrl || $[18] !== t10 || $[19] !== t8) {
    t11 = <LazyImage src={t7} alt={t8} className="absolute inset-0 size-full" fallback={t10} />;
    $[17] = example.imageUrl;
    $[18] = t10;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== example.attributes) {
    t12 = <div className="absolute left-3 top-3"><JourneyTransitionBadge attributes={example.attributes} /></div>;
    $[21] = example.attributes;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t11 || $[24] !== t12) {
    t13 = <div className="relative aspect-4/3 w-full overflow-hidden bg-linear-to-br from-gray-50 to-gray-100">{t11}{t12}</div>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== postMeta) {
    t14 = postMeta?.caption && <p className="text-sm leading-relaxed text-gray-600">{postMeta.caption}</p>;
    $[26] = postMeta;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Heart className="size-3.5" aria-hidden="true" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = postMeta?.likes ?? "\u2014";
  let t17;
  if ($[29] !== t16) {
    t17 = <span className="inline-flex items-center gap-1">{t15}{t16}</span>;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <MessageCircle className="size-3.5" aria-hidden="true" />;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = postMeta?.comments ?? "\u2014";
  let t20;
  if ($[32] !== t19) {
    t20 = <span className="inline-flex items-center gap-1">{t18}{t19}</span>;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Send className="size-3.5" aria-hidden="true" />;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  const t22 = postMeta?.shares ?? "\u2014";
  let t23;
  if ($[35] !== t22) {
    t23 = <span className="inline-flex items-center gap-1">{t21}{t22}</span>;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t17 || $[38] !== t20 || $[39] !== t23) {
    t24 = <div className="flex items-center gap-4 text-xs text-gray-400">{t17}{t20}{t23}</div>;
    $[37] = t17;
    $[38] = t20;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t14 || $[42] !== t24) {
    t25 = <div className="flex flex-col gap-2 px-3.5 py-3">{t14}{t24}</div>;
    $[41] = t14;
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== t13 || $[45] !== t25 || $[46] !== t6) {
    t26 = <div className="flex flex-col overflow-hidden rounded-lg border border-gray-200">{t6}{t13}{t25}</div>;
    $[44] = t13;
    $[45] = t25;
    $[46] = t6;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== analysis) {
    t27 = analysis && <><div><p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Analysis</p><p className="mt-0.5 text-base font-bold text-gray-900">{analysis.performanceLabel}</p></div>{analysis.metrics.length > 0 && <div className="flex flex-wrap items-center gap-3">{analysis.metrics.map(_temp3)}</div>}{analysis.whyItWorked.length > 0 && <div><p className="text-xs font-black uppercase tracking-wider text-gray-900">Why it worked</p><ul className="mt-1.5 flex flex-col gap-1">{analysis.whyItWorked.map(_temp4)}</ul></div>}{analysis.audienceSignal && <div><p className="text-[10px] font-black uppercase tracking-wider text-[#d63f20]">Audience signal</p><p className="mt-1 text-sm leading-relaxed text-gray-700">{analysis.audienceSignal}</p></div>}</>;
    $[48] = analysis;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== analysis || $[51] !== example.whatTheyDid || $[52] !== example.whyItWorks) {
    t28 = !analysis && <div className="flex flex-col gap-3"><div><div className="flex items-center gap-1.5"><Target className="size-3 shrink-0 text-[#d63f20]" aria-hidden="true" /><p className="text-[10px] font-black uppercase tracking-wider text-[#d63f20]">What they did</p></div><p className="mt-1 text-sm leading-relaxed text-gray-600">{example.whatTheyDid}</p></div><div><div className="flex items-center gap-1.5"><Lightbulb className="size-3 shrink-0 text-[#159a84]" aria-hidden="true" /><p className="text-[10px] font-black uppercase tracking-wider text-[#159a84]">Why it works</p></div><p className="mt-1 text-sm leading-relaxed text-gray-600">{example.whyItWorks}</p></div></div>;
    $[50] = analysis;
    $[51] = example.whatTheyDid;
    $[52] = example.whyItWorks;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== example.attributes) {
    t29 = <div className="mt-auto flex flex-col gap-2 pt-1"><DemandTaxonomyChips attributes={example.attributes} /><ShowProvidersLink attributes={example.attributes} /></div>;
    $[54] = example.attributes;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t27 || $[57] !== t28 || $[58] !== t29) {
    t30 = <div className="flex flex-col gap-3 overflow-hidden">{t27}{t28}{t29}</div>;
    $[56] = t27;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t2 || $[61] !== t26 || $[62] !== t30) {
    t31 = <article className={t2}>{t26}{t30}</article>;
    $[60] = t2;
    $[61] = t26;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  return t31;
}
function _temp4(point) {
  return <li key={point} className="text-sm leading-relaxed text-gray-500"><span className="mr-1.5 text-gray-300">•</span>{point}</li>;
}
function _temp3(metric) {
  return <div key={metric.label} className={cn("rounded-md border px-3 py-1.5 text-center", metric.colorClass ?? "border-gray-200 text-gray-700")}><p className="text-sm font-black">{metric.value}</p><p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{metric.label}</p></div>;
}